import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, ArrowUpRight, Play, X } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURED = [
  { src: "/realisations/p5.jpg", label: "Charpente lamellé-collé" },
  { src: "/realisations/p4.jpg", label: "Rénovation colombages & ardoise" },
  { src: "/realisations/p3.jpg", label: "Essentage ardoise en cours" },
  { src: "/realisations/p2.jpg", label: "Bardage bâtiment tertiaire" },
  { src: "/realisations/p1.jpg", label: "L'équipe sur chantier" },
];

export default function Realisations() {
  const [posts, setPosts] = useState([]);
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/instagram/posts?limit=9`)
      .then((r) => setPosts(r.data.data || []))
      .catch(() => setPosts([]));
  }, []);

  return (
    <section id="realisations" data-testid="realisations-section" className="py-28 lg:py-40 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16"
        >
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-jaune font-bold mb-4">Réalisations</p>
            <h2 className="font-display font-bold uppercase tracking-tight text-3xl sm:text-4xl md:text-5xl leading-tight">
              Le chantier ne s'arrête<br className="hidden md:block" /> jamais
            </h2>
          </div>
          <a
            href="https://www.instagram.com/goujon_vallee"
            target="_blank"
            rel="noreferrer"
            data-testid="instagram-follow-link"
            className="group flex items-center gap-3 text-xs tracking-[0.2em] uppercase text-zinc-400 hover:text-jaune transition-colors duration-300"
          >
            <Instagram className="w-5 h-5" />
            Suivez-nous sur Instagram
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </motion.div>

        <p className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold mb-4">Nos chantiers du moment</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-1 mb-16" data-testid="featured-grid">
          {FEATURED.map((f, i) => (
            <motion.div
              key={f.src}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="group relative aspect-[3/4] overflow-hidden bg-[#121214]"
              data-testid={`featured-item-${i}`}
            >
              <img
                src={f.src}
                alt={`${f.label} — Goujon Vallée`}
                loading="lazy"
                className="w-full h-full object-cover grayscale transition-[filter,transform] duration-700 group-hover:grayscale-0 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-4">
                <p className="text-xs text-zinc-100 font-bold uppercase tracking-wider">{f.label}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <p className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold mb-4">En direct d'Instagram</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-1" data-testid="realisations-grid">
          {posts.map((post, i) => (
            <motion.a
              key={post.id}
              href={post.permalink || "https://www.instagram.com/goujon_vallee"}
              target="_blank"
              rel="noreferrer"
              data-testid={`realisation-item-${i}`}
              onClick={(e) => {
                if (post.media_type === "VIDEO" && post.media_url) {
                  e.preventDefault();
                  setPlaying(post);
                }
              }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
              className="group relative aspect-square overflow-hidden bg-[#121214]"
            >
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-zinc-600">
                <Instagram className="w-8 h-8" />
                <span className="text-[10px] tracking-[0.2em] uppercase">Voir sur Instagram</span>
              </div>
              <img
                src={`${API}/instagram/image/${post.id}`}
                alt={post.caption || "Réalisation Goujon Vallée en Normandie"}
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                className="absolute inset-0 w-full h-full object-cover grayscale transition-[filter,transform] duration-700 group-hover:grayscale-0 group-hover:scale-105"
              />
              {post.media_type === "VIDEO" && (
                <div className="absolute top-3 right-3 z-10 bg-black/60 backdrop-blur p-2" data-testid={`realisation-video-badge-${i}`}>
                  <Play className="w-4 h-4 text-white" />
                </div>
              )}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-5">
                <p className="text-xs text-zinc-200 leading-relaxed line-clamp-3">{post.caption}</p>
              </div>
            </motion.a>
          ))}
          {posts.length === 0 && (
            <p className="col-span-full text-zinc-500 text-sm py-20 text-center" data-testid="realisations-empty">
              Nos réalisations arrivent très bientôt.
            </p>
          )}
        </div>
      </div>

      <AnimatePresence>
        {playing && (
          <motion.div
            data-testid="video-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/90 backdrop-blur flex items-center justify-center p-6"
            onClick={() => setPlaying(null)}
          >
            <button
              data-testid="video-modal-close"
              onClick={() => setPlaying(null)}
              className="absolute top-6 right-6 text-white p-2 hover:text-jaune transition-colors duration-300"
              aria-label="Fermer la vidéo"
            >
              <X className="w-7 h-7" />
            </button>
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="max-w-3xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <video
                src={`${API}/instagram/video/${playing.id}`}
                controls
                autoPlay
                muted
                playsInline
                className="w-full max-h-[75vh] bg-black"
                data-testid="video-modal-player"
              />
              {playing.caption && (
                <p className="mt-4 text-sm text-zinc-300 leading-relaxed">{playing.caption}</p>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
